import { Auth0Provider, AppState } from "@auth0/auth0-react";
import React, { PropsWithChildren } from "react";
import { useNavigate } from "react-router-dom";
import { getLogger } from './utils/logger';

const logger = getLogger('auth0-provider-with-navigate')

interface Auth0ProviderWithNavigateProps {
  children: React.ReactNode;
}

export const Auth0ProviderWithNavigate = ({
  children,
}: PropsWithChildren<Auth0ProviderWithNavigateProps>): JSX.Element | null => {
  const navigate = useNavigate();

  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_AUTH0_CALLBACK_URL;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;


  if (!(domain && clientId && redirectUri)) {
    logger.error("Missing Auth0 configuration. Check environment variables.");
    return null;
  }

  if (!audience) {
    logger.warn("Auth0 audience is missing! API authorization will not work correctly.");
  }

  const onRedirectCallback = (appState?: AppState) => {
    // Redirect to the dashboard after successful authentication
    // If there's a specific returnTo in appState, use that instead
    navigate(appState?.returnTo || "/dashboard");
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      // Refresh tokens instead of hidden-iframe silent auth. The iframe needs
      // Auth0's session cookie in a third-party context, which Safari and other
      // ITP browsers block — and the SDK answers that blocked iframe
      // (login_required) by logging the user out locally, which unmounts every
      // guarded page mid-flow. A refresh token renews via a direct POST to
      // /oauth/token, no iframe, so forced refreshes (the promo claim flow) and
      // full page loads work in every browser. localStorage persistence is what
      // lets a full-page navigation keep the session; the XSS trade-off is
      // recorded in docs/promo_grants.md. Requires, in the Auth0 dashboard:
      // "Allow Offline Access" on the API, and the Refresh Token grant with
      // rotation enabled on this application.
      useRefreshTokens={true}
      // Without the fallback, a session holding no refresh token is a hard
      // sign-out: the SDK throws MissingRefreshTokenError instead of renewing.
      // That is every already-signed-in session at the moment this deploy
      // lands (their tokens were minted without offline_access), and every
      // user whose refresh token has expired or been revoked since. The
      // fallback retries those through the iframe — Safari still takes the
      // refresh-token path first, so the ITP fix above is unaffected.
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: redirectUri,
        scope: "openid profile email offline_access",
        ...(audience && { audience }),
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};